import React, { Fragment } from 'react';
import { Field, Formik } from 'formik';
import _ from 'lodash';
import Yup from 'yup';

import { isEmpty } from 'buyside/util/lodashAlt';

import styles from '../onboardingFormStyles.scss';

const {
  sectorNames,
  geographyNames,
  assetClassNames,
  fundTypeNames,
  fundStrategyNames,
  aumRangeNames
} = require('buyside/util/constantsFromRails.json');

import CheckboxesField from 'buyside/components/presentation/Form/Field/CheckboxesField/checkboxesField';
import Button, { BUTTON_TYPES } from 'buyside/components/presentation/Button/button';

import { useIsMobile } from 'buyside/util/hooks/useIsMobile';

const OnboardingWatchlistForm = ({ handleSubmit, watchlist, isAllocator }) => {
  const {
    sectors = [],
    geographies = [],
    assetClasses = [],
    fundTypes = [],
    fundStrategies = [],
    aumRanges = []
  } = watchlist;
  const sectorNames = sectors.map(sector => sector.name);
  const geographyNames = geographies.map(geography => geography.name);
  const assetClassNames = assetClasses.map(assetClass => assetClass.name);
  const fundTypeNames = fundTypes.map(fundType => fundType.name);
  const fundStrategyNames = fundStrategies.map(fundStrategy => fundStrategy.name);
  const aumRangeNames = aumRanges.map(aumRange => aumRange.name);

  return (
    <div className={styles.onboardingMainArea}>
      <h2 className={styles.onboardingFormHeader}>Required: Customize Your SumZero Experience</h2>
      <div className={styles.onboardingFormInfo}>
        To improve your SumZero experience, we require you to identify your investment preferences.
        This will take 2-3 minutes.
      </div>
      <Formik
        initialValues={{
          sectors: sectorNames,
          geographies: geographyNames,
          assetClasses: assetClassNames,
          fundTypes: fundTypeNames,
          fundStrategies: fundStrategyNames,
          aumRanges: aumRangeNames
        }}
        onSubmit={handleSubmit}
        validationSchema={Yup.object().shape({
          sectors: Yup.array().required(
            'Select at least one checkbox from each section to proceed.'
          ),
          geographies: Yup.array().required(
            'Select at least one checkbox from each section to proceed.'
          ),
          assetClasses: Yup.array().required(
            'Select at least one checkbox from each section to proceed.'
          )
        })}
      >
        {props => <_onboardingWatchlistFormikForm {...props} isAllocator={isAllocator} />}
      </Formik>
    </div>
  );
};

const _onboardingWatchlistFormikForm = props => {
  const isMobile = useIsMobile();
  const { handleSubmit, values, isSubmitting, isAllocator } = props;
  let missingValues =
    isEmpty(values.sectors) || isEmpty(values.geographies) || isEmpty(values.assetClasses);

  if (isAllocator) {
    missingValues =
      missingValues ||
      isEmpty(values.fundTypes) ||
      isEmpty(values.fundStrategies) ||
      isEmpty(values.aumRanges);
  }

  const sectorNamesOrdered = sectorNames.slice(0);
  _.remove(sectorNamesOrdered, name => name === 'Generalist' || name === 'Unclassifiable');
  sectorNamesOrdered.unshift('Generalist');

  return (
    <form onSubmit={handleSubmit}>
      <div className={styles.formFieldArea}>
        <h3 className={styles.fieldHeader}>
          1. What sectors do you want to follow? - <em>Check all that apply.</em>
        </h3>
        <Field
          component={CheckboxesField}
          name="sectors"
          options={sectorNamesOrdered}
          numColumns={2}
          checkboxLabelClassName={styles.checkboxLabel}
          customLabelWrapperClassName={styles.checkboxLabelWrapper}
          checkAllField="Generalist"
          customLabels={{ Generalist: '(Selects all)' }}
        />
      </div>
      <div className={styles.formFieldArea}>
        <h3 className={styles.fieldHeader}>
          2. What geographies do you want to follow? - <em>Check all that apply.</em>
        </h3>
        <Field
          component={CheckboxesField}
          name="geographies"
          options={geographyNames}
          numColumns={2}
          checkboxLabelClassName={styles.checkboxLabel}
          customLabelWrapperClassName={styles.checkboxLabelWrapper}
        />
      </div>
      <div className={styles.formFieldArea}>
        <h3 className={styles.fieldHeader}>
          3. What asset classes do you want to follow? - <em>Check all that apply.</em>
        </h3>
        <Field
          component={CheckboxesField}
          name="assetClasses"
          options={assetClassNames}
          numColumns={2}
          checkboxLabelClassName={styles.checkboxLabel}
          customLabelWrapperClassName={styles.checkboxLabelWrapper}
        />
      </div>
      {isAllocator && (
        <Fragment>
          <div className={styles.formFieldArea}>
            <h3 className={styles.fieldHeader}>
              4. Which types of funds do you want to follow? - <em>Check all that apply.</em>
            </h3>
            <Field
              component={CheckboxesField}
              name="fundTypes"
              options={fundTypeNames}
              numColumns={isMobile ? 2 : 3}
              checkboxLabelClassName={styles.checkboxLabel}
              customLabelWrapperClassName={styles.checkboxLabelWrapper}
            />
          </div>
          <div className={styles.formFieldArea}>
            <h3 className={styles.fieldHeader}>
              5. Which fund investment strategies fit your firm's mandate? - &nbsp;
              <em>Check all that apply.</em>
            </h3>
            <Field
              component={CheckboxesField}
              name="fundStrategies"
              options={fundStrategyNames}
              numColumns={isMobile ? 2 : 3}
              checkboxLabelClassName={styles.checkboxLabel}
              customLabelWrapperClassName={styles.checkboxLabelWrapper}
            />
          </div>
          <div className={styles.formFieldArea}>
            <h3 className={styles.fieldHeader}>
              6. Which size funds (by AUM) are you interested in working with? - &nbsp;
              <em>Check all that apply.</em>
            </h3>
            <Field
              component={CheckboxesField}
              name="aumRanges"
              options={aumRangeNames}
              numColumns={isMobile ? 2 : 3}
              checkboxLabelClassName={styles.checkboxLabel}
              customLabelWrapperClassName={styles.checkboxLabelWrapper}
            />
          </div>
        </Fragment>
      )}
      <div className={styles.nextSectionNav}>
        <Button
          text="Next Section - Add Tickers"
          type={BUTTON_TYPES.PRIMARY}
          isLoading={isSubmitting}
          disabled={missingValues}
          id="NextButton"
        />
        {missingValues && (
          <div className={styles.validationText}>
            Select at least one checkbox from each section to proceed.
          </div>
        )}
      </div>
    </form>
  );
};

export default OnboardingWatchlistForm;
